import React, { useState, useMemo } from 'react';
import { ChevronDown, LogOut, Menu, Home, BarChart2, Users, DollarSign, Share2, Link, BookOpen, Settings, HelpCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SubdomainAppData } from '../../utils/service';
import { COLORS } from '../../utils/constants';
import { adjustBrightness } from '../../utils/colors';

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
}

interface MenuGroup {
  title: string;
  items: MenuItem[];
}

interface AffiliateDashboardLayoutProps {
  app: SubdomainAppData;
  userEmail: string;
  menuGroups: MenuGroup[];
  selectedMenuItem: string;
  onLogout: () => void;
  children: React.ReactNode;
  disableSidebar?: boolean;
}

const AffiliateDashboardLayout: React.FC<AffiliateDashboardLayoutProps> = ({
  app,
  userEmail,
  menuGroups,
  selectedMenuItem,
  onLogout,
  children,
  disableSidebar
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const navigate = useNavigate();

  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const getInitial = (email: string) => email.charAt(0).toUpperCase();

  const getSelectedMenuItemTitle = () => {
    for (const group of menuGroups) {
      const selectedItem = group.items.find(item => item.id === selectedMenuItem);
      if (selectedItem) return selectedItem.label;
    }
    return '';
  };

  const getSelectedMenuItemIcon = () => {
    for (const group of menuGroups) {
      const selectedItem = group.items.find(item => item.id === selectedMenuItem);
      if (selectedItem) return selectedItem.icon;
    }
    return undefined;
  };

  // Generate color shades based on brand_color
  const colors = useMemo(() => {
    const brandColor = app.brand_color || COLORS.primary;
    const brandTextColor = app.brand_text_color || COLORS.text
    return {
      primary: brandColor,
      primaryHover: adjustBrightness(brandColor, -10),
      background: COLORS.background,
      textColor: brandTextColor,
    };
  }, [app.brand_color]);

  return (
    <div className="d-flex vh-100" style={{ backgroundColor: colors.background }}>
      {/* Sidebar */}
      <div 
        className={`d-flex flex-column flex-shrink-0 text-white ${isSidebarOpen ? 'd-block' : 'd-none'} d-md-block`} 
        style={{ width: '280px', backgroundColor: colors.primary }}
      >
        <div className="d-flex flex-column h-100">
          {/* Portal Name with Logo */}
          <a href="/" className="d-flex align-items-center text-white text-decoration-none px-3 py-4">
            {app.brand_url && (
              <img src={app.brand_url} alt="Logo" className="me-2" style={{ width: '24px', height: '24px' }} />
            )}
            <span className="fs-5 fw-semibold" style={{ color: app.brand_text_color || COLORS.text}}>{app.app_name}</span>
          </a>
          
          {/* Menu Groups */}
          <nav className="flex-grow-1 overflow-auto">
            {menuGroups.map((group, index) => (
              <div key={index} className="mb-3">
                <p className="fs-7 ms-3 mt-3 mb-2" style={{ color: colors.textColor }}>{group.title}</p>
                <ul className="nav flex-column mb-auto px-2">
                  {group.items.map((item) => (
                    <li className="nav-item my-1" key={item.id}>
                      <button
                        className={`nav-link text-white w-100 text-start rounded px-3 py-2 d-flex align-items-center ${selectedMenuItem === item.id ? 'fw-semibold' : ''}`}
                        style={{ 
                          backgroundColor: selectedMenuItem === item.id && !disableSidebar ? colors.primaryHover : 'transparent',
                          transition: 'background-color 0.2s',
                        }}
                        onClick={() => navigate(`/dashboard/${item.id}`) }
                        disabled={disableSidebar}
                      >
                        {item.icon}
                        <span className="ms-2" style={{
                          color: colors.textColor,
                        }}>{item.label}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>

          {/* User Info and Logout Dropdown */}
          <div className="mt-auto border-top" style={{ borderColor: `${colors.primaryHover} !important` }}>
            <div className="dropdown px-3 py-3">
              <a href="#" className="d-flex align-items-center text-white text-decoration-none dropdown-toggle" onClick={toggleDropdown}>
                <div 
                  className="rounded-circle me-2 d-flex justify-content-center align-items-center"
                  style={{
                    width: '36px',
                    height: '36px',
                    backgroundColor: colors.primaryHover,
                    color: colors.textColor,
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                >
                  {getInitial(userEmail)}
                </div>
                <div className="d-flex flex-column me-2">
                  <strong style={{ color: colors.textColor }} className="fs-6">{userEmail.split('@')[0]}</strong>
                  <small style={{ color: colors.textColor }}>{userEmail}</small>
                </div>
                <ChevronDown size={20} className="ms-auto" />
              </a>
              <ul className={`dropdown-menu dropdown-menu-dark text-small shadow${isDropdownOpen ? ' show' : ''}`} 
                  style={{ backgroundColor: colors.primaryHover, position: 'absolute', bottom: '100%', left: '20px' }}>
                <li><a className="dropdown-item" href="#" onClick={onLogout}>Sign out</a></li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-grow-1 d-flex flex-column overflow-hidden">
        {/* Header */}
        <header className="p-3 border-bottom" style={{ backgroundColor: colors.background }}>
          <div className="container-fluid d-flex justify-content-between align-items-center">
            <button 
              className="btn btn-link d-md-none p-0 text-decoration-none"
              type="button" 
              onClick={toggleSidebar}
            >
              <Menu size={24} color={colors.primary} />
            </button>
            <div className="d-flex">
              {
                getSelectedMenuItemIcon() && (
                  <span className="me-2">{getSelectedMenuItemIcon()}</span>
                )
              }
              <h1 className="h4 mb-0 text-md-start w-100" style={{ color: COLORS.text }}>
                {disableSidebar ? 'Get Started' : getSelectedMenuItemTitle()}
              </h1>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-grow-1 overflow-auto px-3">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AffiliateDashboardLayout;